import moment from 'moment';
import axiosAdmin from '../../services/axiosAdmin';
import { concluirEnvio, obterInspecao } from './inspecaoDb';

const MAXIMO_NOVAS_TENTATIVAS = 3;
const TEMPO_LIMITE_ENVIO = 120000;
const LADO_MAXIMO_FOTO = 1920;
const QUALIDADE_FOTO = 0.75;

const aguardar = (tempo) => new Promise((resolve) => { setTimeout(resolve, tempo); });

const possuiCodigo12002 = (erro) => {
  if (String(erro?.code) === '12002' || /12002/.test(erro?.message || '')) return true;
  try {
    return /12002/.test(JSON.stringify(erro?.response?.data || ''));
  } catch (e) {
    return false;
  }
};

const erroDeTempoEsgotado = (erro) => (
  erro?.code === 'ECONNABORTED'
  || /timeout|timed out/i.test(erro?.message || '')
  || possuiCodigo12002(erro)
  || erro?.response?.status === 408
  || erro?.response?.status === 504
);

/* ---------------------------------------------------------------- Placa */

export const formatarPlaca = (texto) => {
  const valor = String(texto || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
  return valor.length > 3 ? `${valor.slice(0, 3)}-${valor.slice(3)}` : valor;
};

export const placaValida = (placa) => /^[A-Z]{3}-[0-9][A-Z][0-9]{2}$/.test(placa) || /^[A-Z]{3}-[0-9]{4}$/.test(placa);

/* ---------------------------------------------------------------- Fotos */

const carregarImagem = (arquivo) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(arquivo);
  const imagem = new Image();
  imagem.onload = () => { URL.revokeObjectURL(url); resolve(imagem); };
  imagem.onerror = () => { URL.revokeObjectURL(url); reject(new Error('IMAGEM_INVALIDA')); };
  imagem.src = url;
});

/**
 * Reduz a foto (lado maior até 1920px, JPEG 75%) antes de guardar,
 * equivalente ao quality 0.75 usado pela câmera no app React Native.
 * O navegador já aplica a orientação EXIF ao desenhar a imagem.
 */
export const comprimirFoto = async (arquivo) => {
  const imagem = await carregarImagem(arquivo);
  const escala = Math.min(1, LADO_MAXIMO_FOTO / Math.max(imagem.naturalWidth, imagem.naturalHeight));
  const largura = Math.round(imagem.naturalWidth * escala);
  const altura = Math.round(imagem.naturalHeight * escala);

  const canvas = document.createElement('canvas');
  canvas.width = largura;
  canvas.height = altura;
  canvas.getContext('2d').drawImage(imagem, 0, 0, largura, altura);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob || arquivo), 'image/jpeg', QUALIDADE_FOTO);
  });
};

/* ---------------------------------------------------------------- Envio */

const enviarFormulario = (idUsuario, inspecaoId, placa, criadoEm, fotos, aoProgredir) => {
  const formulario = new FormData();
  formulario.append('idUsuario', String(idUsuario));
  formulario.append('placa', placa);
  formulario.append('inspecaoId', String(inspecaoId));
  formulario.append('criadoEm', moment(criadoEm).format('DD-MM-YYYY HH:mm:ss'));
  formulario.append('recebidoEm', moment().format('DD-MM-YYYY HH:mm:ss'));
  fotos.forEach((foto, indice) => {
    formulario.append('fotos', foto.blob, `${placa.replace('-', '')}_${indice + 1}.jpg`);
  });

  return axiosAdmin.post('/oficinaapp', formulario, {
    timeout: TEMPO_LIMITE_ENVIO,
    headers: { Accept: 'application/json' },
    onUploadProgress: (evento) => {
      if (aoProgredir && evento.total) aoProgredir(Math.round((evento.loaded * 100) / evento.total));
    },
  });
};

export const enviarInspecao = async (idUsuario, inspecaoId, placa, fotos, aoProgredir) => {
  const inspecao = await obterInspecao(inspecaoId);
  if (!inspecao) throw new Error('INSPECAO_NAO_ENCONTRADA');

  for (let tentativa = 0; tentativa <= MAXIMO_NOVAS_TENTATIVAS; tentativa += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await enviarFormulario(idUsuario, inspecaoId, placa, inspecao.criadoEm, fotos, aoProgredir);
      break;
    } catch (erro) {
      erro.tentativasEnvio = tentativa + 1;
      if (!erroDeTempoEsgotado(erro) || tentativa === MAXIMO_NOVAS_TENTATIVAS) throw erro;
      // eslint-disable-next-line no-await-in-loop
      await aguardar((tentativa + 1) * 1500);
    }
  }

  await concluirEnvio(inspecaoId);
};

const limitarDetalhe = (valor) => {
  if (typeof valor !== 'string') return '';
  return valor.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300);
};

export const obterMensagemErroEnvio = (erro) => {
  if (erro?.message === 'INSPECAO_NAO_ENCONTRADA') {
    return {
      titulo: 'Inspeção não encontrada',
      mensagem: 'Não foi possível localizar esta inspeção neste navegador. Inicie uma nova inspeção e tente novamente.',
    };
  }

  const status = erro?.response?.status;

  if (erroDeTempoEsgotado(erro)) {
    return {
      titulo: 'Tempo de conexão esgotado',
      mensagem: `O servidor demorou para responder. Foram realizadas ${erro?.tentativasEnvio || 1} tentativa(s), mas o envio não foi concluído. Verifique a internet e tente novamente. As fotos continuam salvas neste aparelho.`,
    };
  }

  if (!status) {
    const detalhe = limitarDetalhe(erro?.message);
    return {
      titulo: navigator.onLine === false ? 'Internet desconectada' : 'Servidor inacessível',
      mensagem: navigator.onLine === false
        ? 'O aparelho está sem acesso à internet. Ative o Wi-Fi ou os dados móveis antes de enviar. As fotos continuam salvas neste aparelho.'
        : `Não foi possível acessar o servidor do eloOficina.${detalhe ? ` Detalhe: ${detalhe}.` : ''} Verifique a conexão e tente novamente. As fotos continuam salvas neste aparelho.`,
    };
  }

  const dados = erro.response.data;
  const detalheServidor = limitarDetalhe(
    typeof dados === 'string' ? dados : dados?.mensagem || dados?.message || dados?.erro || dados?.error || dados?.detalhes,
  );

  if (status === 401 || status === 403) {
    return {
      sessaoExpirada: true,
      titulo: 'Sessão inválida',
      mensagem: 'Seu acesso expirou ou não possui autorização para enviar a inspeção. Entre novamente e repita o envio. As fotos continuam salvas neste aparelho.',
    };
  }

  if (status === 413) {
    return {
      titulo: 'Fotos muito grandes',
      mensagem: 'O servidor recusou o envio porque o conjunto de fotos ultrapassou o tamanho permitido. Remova algumas fotos ou tente novamente com menos imagens.',
    };
  }

  if (status >= 500) {
    return {
      titulo: 'Servidor indisponível',
      mensagem: `O servidor apresentou uma falha (código ${status}).${detalheServidor ? ` Detalhe: ${detalheServidor}.` : ''} Tente novamente em alguns minutos. As fotos continuam salvas neste aparelho.`,
    };
  }

  return {
    titulo: 'Não foi possível enviar',
    mensagem: `O servidor recusou o envio (código ${status}).${detalheServidor ? ` Detalhe: ${detalheServidor}.` : ''} Revise as informações e tente novamente. As fotos continuam salvas neste aparelho.`,
  };
};
